import React, { FC, useEffect, useState } from 'react'
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import GlobalFiterSelectItem from './GlobalFiterSelectItem';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import useUserByEmail from '../../../../store/ReactQueries/users/useUserByEmail';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import useGetFieldsToFilter from '../../../../store/ReactQueries/globalFilters/useGetFieldsToFilter';
import useRemoveUserGlobalFilter from '../../../../store/ReactQueries/globalFilters/useRemoveUserGlobalFilter';
import useGetAllUserGlobalFilters from '../../../../store/ReactQueries/globalFilters/useGetAllUserGlobalFilters';
import { useUserStore } from '../../../../store/useUserStore';
import { useLanguageStore } from '../../../../store/useLanguageStore';
import { FieldsToFilterInterface } from '../../../../models/UserModel/UserGlobalFilter';

export interface GlobalFiltersSelectProps {
    email: string | undefined;
}

export const GlobalFiltersSelect: FC<GlobalFiltersSelectProps> = ({ email }) => {

    const { user } = useUserStore();
    const userId = user?.userId ?? '';

    const isEmployee = user?.['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] === 'Employee';

    const { language } = useLanguageStore();
    const { data: userByEmail } = useUserByEmail(email || '');
    const { data: fieldsToFilter } = useGetFieldsToFilter();
    const { data: globalFilters } = useGetAllUserGlobalFilters(userByEmail && userByEmail.id ? userByEmail.id : userId);
    const removeFilter = useRemoveUserGlobalFilter(userByEmail && userByEmail.id ? userByEmail.id : userId);

    const [filterModal, setFilterModal] = useState<boolean>(false);
    const [filters, setFilters] = useState<FieldsToFilterInterface>();
    const [hiddenFilters, setHiddenFilters] = useState<FieldsToFilterInterface>();

    useEffect(() => {
        if (fieldsToFilter && globalFilters && globalFilters.length > 0) {
            const filteredKeys = new Set(globalFilters.map((filter) => filter.fieldName));

            const filteredFields = Object.fromEntries(
                Object.entries(fieldsToFilter).filter(
                    ([key]) => filteredKeys.has(key)
                )
            );
            const hiddenFilteredFields = Object.fromEntries(
                Object.entries(fieldsToFilter).filter(
                    ([key]) => !filteredKeys.has(key)
                )
            );
            setFilters(filteredFields);
            setHiddenFilters(hiddenFilteredFields);
        } else {
            setFilters(undefined);
            setHiddenFilters(fieldsToFilter);
        }
    }, [userByEmail, userId, fieldsToFilter, globalFilters]);

    const handleHiddenFilters = (key: string) => {
        if (!fieldsToFilter) {
            console.error('fieldsToFilter is undefined');
            return;
        }

        if (!filters || !hiddenFilters) {
            // Initialize filters and hiddenFilters if they are undefined
            const newFilters = { [key]: fieldsToFilter[key] };
            const newHiddenFilters = { ...fieldsToFilter };
            delete newHiddenFilters[key];

            setFilters(newFilters);
            setHiddenFilters(newHiddenFilters);
        } else {
            // Existing logic for updating filters and hiddenFilters
            const updatedFilters = { ...filters, [key]: fieldsToFilter[key] };
            const { [key]: _, ...remainingHiddenFilters } = hiddenFilters;

            setFilters(updatedFilters);
            setHiddenFilters(remainingHiddenFilters);
        }
    };

    const handleDelete = (key: string) => {
        if (filters && hiddenFilters && fieldsToFilter) {
            const { [key]: removedFilter, ...remainingFilters } = filters;
            const updatedHiddenFilters = { ...hiddenFilters, [key]: fieldsToFilter[key] };

            const filtersById = globalFilters?.find(item => item.fieldName === key);
            if (filtersById && filtersById.id) removeFilter.mutate(filtersById.id);
            setFilters(remainingFilters);
            setHiddenFilters(updatedHiddenFilters);
        }
    };

    return <React.Fragment>
        {filters
            && Object.keys(filters).length > 0
            && <div className='grid-inputs-2'>
                {Object.entries(filters).map(([key], index) => (
                    <div
                        key={`Global-filters-${index}`}
                        className='w-100 d-flex flex-row gap-1 align-items-center'
                    >
                        <GlobalFiterSelectItem
                            fieldName={key}
                            userId={userByEmail && userByEmail.id ? userByEmail.id : userId}
                        />
                        {!isEmployee
                            && <IconButton
                                aria-label="edit"
                                title='Edit Item'
                                color="error"
                                onClick={() => handleDelete(key)}
                                className='mt-4'
                            >
                                <DeleteOutlineOutlinedIcon />
                            </IconButton>}
                    </div>
                ))}
            </div >}
        {hiddenFilters
            && !isEmployee
            && Object.keys(hiddenFilters).length > 0
            && <div style={{ padding: "15px 30px" }}>
                <Button
                    variant="contained"
                    color='primary'
                    onClick={() => setFilterModal(!filterModal)}
                >
                    {
                        filterModal
                            ? <RemoveCircleOutlineIcon className='me-1' />
                            : <AddCircleIcon className='me-1' />
                    }
                    {
                        filterModal
                            ? language['RemoveFilter']
                            : language['AddFilter']
                    }
                </Button>
            </div>}

        {
            filterModal
            && <div className='grid-inputs-2'>
                {
                    hiddenFilters
                    && Object.entries(hiddenFilters)
                        .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
                        .map(([key, value]) => (
                            <Button
                                key={key}
                                variant="outlined"
                                color="primary"
                                onClick={() => handleHiddenFilters(key)}
                                style={{ height: 50 }}
                            >
                                {language[value] ?? value}
                            </Button>
                        ))
                }
            </div>
        }
    </React.Fragment>
}
export default GlobalFiltersSelect;